import { cn } from '@/lib/utils';
import { DutyStatus } from '../../../api/src/shared';

/**
 * Utility functions for status color styling
 */

/**
 * Format DutyStatus enum value for display
 * @param status - DutyStatus enum value
 * @returns Human-readable status label
 */
export function formatDutyStatus(status: DutyStatus): string {
  switch (status) {
    case DutyStatus.OnDuty:
      return 'On Duty';
    case DutyStatus.OffDuty:
      return 'Off Duty';
    case DutyStatus.Standby:
      return 'Standby';
    case DutyStatus.Training:
      return 'Training';
    case DutyStatus.Leave:
      return 'Leave';
    case DutyStatus.Sick:
      return 'Sick';
    case DutyStatus.Positioning:
      return 'Positioning';
    default:
      return status;
  }
}

/**
 * Get Tailwind CSS class for roster duty status color
 * @param status - DutyStatus value
 * @returns Tailwind CSS class string for the status color
 */
export function getRosterDutyStatusColor(status: DutyStatus | string): string {
  return cn({
    'bg-status-on-duty': status === DutyStatus.OnDuty,
    'bg-status-standby': status === DutyStatus.Standby,
    'bg-status-training': status === DutyStatus.Training,
    'bg-status-leave': status === DutyStatus.Leave,
    'bg-status-sick': status === DutyStatus.Sick,
    'bg-status-positioning': status === DutyStatus.Positioning,
    'bg-status-off-duty':
      status === DutyStatus.OffDuty ||
      !Object.values(DutyStatus).includes(status as DutyStatus),
  });
}

/**
 * Status colors for roster calendar and UI components
 */
export const ROSTER_STATUS_COLORS: Record<DutyStatus, string> = {
  [DutyStatus.OffDuty]: "bg-surface-L2 text-content-primary-bold hover:bg-surface-L2/80",
  [DutyStatus.OnDuty]: "bg-status-on-duty text-content-primary-bold",
  [DutyStatus.Standby]: "bg-status-standby text-content-primary-bold",
  [DutyStatus.Training]: "bg-status-training text-content-primary-bold",
  [DutyStatus.Leave]: "bg-surface-L2 text-content-primary-bold hover:bg-surface-L2/80",
  [DutyStatus.Sick]: "bg-surface-L2 text-content-primary-bold hover:bg-surface-L2/80",
  [DutyStatus.Positioning]: "bg-status-positioning text-content-primary-bold",
}

/**
 * Status indicator bar colors (for CrewMemberRoster list view)
 */
export const ROSTER_STATUS_BAR_COLORS: Record<DutyStatus, string> = {
  [DutyStatus.OnDuty]: "bg-status-on-duty",
  [DutyStatus.Training]: "bg-status-training",
  [DutyStatus.Standby]: "bg-status-standby",
  [DutyStatus.OffDuty]: "bg-status-off-duty",
  [DutyStatus.Leave]: "bg-status-off-duty",
  [DutyStatus.Sick]: "bg-status-off-duty",
  [DutyStatus.Positioning]: "bg-status-positioning",
}

/**
 * Status badge configuration for profile and other UI components
 */
export interface StatusBadgeConfig {
  text: string
  className: string
}

export const ROSTER_STATUS_BADGES: Record<DutyStatus, StatusBadgeConfig> = {
  [DutyStatus.OnDuty]: {
    text: "On Duty",
    className: "bg-status-on-duty text-white",
  },
  [DutyStatus.OffDuty]: {
    text: "Off Duty",
    className: "bg-status-off-duty text-white",
  },
  [DutyStatus.Standby]: {
    text: "Standby",
    className: "bg-status-standby text-white",
  },
  [DutyStatus.Training]: {
    text: "Training",
    className: "bg-status-training text-white",
  },
  [DutyStatus.Leave]: {
    text: "Leave",
    className: "bg-status-off-duty text-white",
  },
  [DutyStatus.Sick]: {
    text: "Sick",
    className: "bg-status-off-duty text-white",
  },
  [DutyStatus.Positioning]: {
    text: "Positioning",
    className: "bg-status-positioning text-white",
  },
}

/**
 * Get the calendar color class for a duty status
 */
export function getRosterStatusColor(status: DutyStatus): string {
  return ROSTER_STATUS_COLORS[status] || ROSTER_STATUS_COLORS[DutyStatus.OffDuty]
}

/**
 * Get the status bar color for a duty status (for list views)
 */
export function getRosterStatusBarColor(status: DutyStatus): string {
  return ROSTER_STATUS_BAR_COLORS[status] || ROSTER_STATUS_BAR_COLORS[DutyStatus.OffDuty]
}

/**
 * Get the badge configuration for a duty status
 */
export function getRosterStatusBadge(status: DutyStatus | null): StatusBadgeConfig {
  if (!status) {
    return {
      text: "Off",
      className: "bg-content-primary-subtle text-white",
    }
  }
  return ROSTER_STATUS_BADGES[status] || {
    text: status,
    className: "bg-content-primary-subtle text-white",
  }
}

/**
 * Get Tailwind CSS classes for aircraft status badges
 * @param status - Aircraft status value
 * @returns Tailwind CSS class string for badge styling
 */
export function getAircraftStatusColor(status: string): string {
  return cn({
    'bg-background-positive-subtle text-content-positive-bold': status === 'Active',
    'bg-background-notice-subtle text-background-notice-bold': status === 'Maintenance',
    'bg-background-negative-subtle text-content-negative-bold': status === 'Inactive',
  });
}

/**
 * Get Tailwind CSS classes for crew document status icons
 * @param status - Document status value ('valid' | 'expiring' | 'expired')
 * @param isActive - Whether the crew member is active
 * @returns Tailwind CSS class string for icon styling
 */
export function getCrewDocumentStatusColor(status: string, isActive: boolean = true): string {
  if (!isActive) {
    return cn('text-content-primary-subtle');
  }

  return cn({
    'text-content-positive-bold': status === 'valid',
    'text-background-notice-bold': status === 'expiring',
    'text-content-negative-bold': status === 'expired',
    'text-content-primary-subtle': !status,
  });
}
